import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toAuthUrl } from "@/components/PhotoUpload";
import {
  Trophy, Target, Activity, Shield, Zap, Clock, TrendingUp, Award, User,
} from "lucide-react";

const PERIODS = [
  { value: "month", label: "Mois en cours" },
  { value: "season", label: "Saison entière" },
];

const CATEGORIES = [
  { value: "ALL", label: "Toutes catégories" },
  { value: "PRO", label: "Senior Pro Dames" },
  { value: "AMATEUR", label: "Senior Amateur Dames" },
  { value: "U17", label: "U17" },
  { value: "U13", label: "U13" },
];

const RANKINGS = [
  { key: "scorers", label: "Top scoreuses", stat: "points", icon: Trophy, color: "emerald", unit: "pts" },
  { key: "passers", label: "Top passeuses", stat: "assists", icon: Target, color: "blue", unit: "pas" },
  { key: "rebounders", label: "Top rebondeuses", stat: "rebounds", icon: Activity, color: "amber", unit: "reb" },
  { key: "defenders", label: "Top défense (contres)", stat: "blocks", icon: Shield, color: "slate", unit: "cnt" },
  { key: "stealers", label: "Top interceptions", stat: "steals", icon: Zap, color: "purple", unit: "int" },
  { key: "minutes", label: "Plus de minutes", stat: "minutes", icon: Clock, color: "slate", unit: "min" },
  { key: "plus_minus", label: "Indice +/-", stat: "plus_minus", icon: TrendingUp, color: "emerald", unit: "" },
];

export default function Classements() {
  const [period, setPeriod] = useState("season");
  const [category, setCategory] = useState("ALL");
  const [data, setData] = useState(null);

  const load = async () => {
    const cat = category === "ALL" ? "" : `&category=${category}`;
    const { data } = await api.get(`/stats/leaderboard?period=${period}${cat}`);
    setData(data);
  };
  useEffect(() => { load(); }, [period, category]);

  if (!data) return <div className="p-8 text-slate-400">Chargement…</div>;

  return (
    <div data-testid="classements-page">
      <PageHeader title="Classements & leaders"
        subtitle={`${data.matches_count} match${data.matches_count > 1 ? "s" : ""} pris en compte`}>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-44" data-testid="period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-52" data-testid="cat-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>

      <div className="p-6 lg:p-8 space-y-6">
        {data.matches_count === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-12 text-center">
            <Trophy className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
            <h3 className="font-barlow text-xl font-bold mb-1">Aucune statistique disponible</h3>
            <p className="text-sm text-slate-500">
              Saisissez les feuilles de match dans le calendrier pour voir les classements.
            </p>
          </div>
        ) : (
          <>
            {/* Top scoreuses spotlight */}
            {data.leaders.scorers.length > 0 && (
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-md p-6 text-white"
                data-testid="top-scorers-spotlight">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-200 mb-3">
                  Performeuse n°1 — {data.leaders.scorers[0].player_name}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
                  <div className="md:col-span-2 flex items-center gap-4">
                    <div className="h-20 w-20 rounded-md border-2 border-white/50 overflow-hidden bg-white/10 flex items-center justify-center shrink-0">
                      {data.leaders.scorers[0].photo_url ? (
                        <img src={toAuthUrl(data.leaders.scorers[0].photo_url)} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <Award className="h-10 w-10 text-emerald-200" />
                      )}
                    </div>
                    <div>
                      <div className="font-barlow font-bold text-3xl tracking-tight leading-none">
                        {data.leaders.scorers[0].player_name}
                      </div>
                      <div className="text-sm text-emerald-100 mt-1">
                        #{data.leaders.scorers[0].jersey_number ?? "—"} • {data.leaders.scorers[0].category} • {data.leaders.scorers[0].position}
                      </div>
                    </div>
                  </div>
                  <SpotlightStat label="Points" value={data.leaders.scorers[0].points} />
                  <SpotlightStat label="Moy/match" value={data.leaders.scorers[0].avg_points} />
                </div>
              </div>
            )}

            {/* Rankings grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {RANKINGS.map((r) => (
                <LeaderboardCard key={r.key} data={data.leaders[r.key]} config={r} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SpotlightStat({ label, value }) {
  return (
    <div className="text-center md:text-left">
      <div className="font-barlow font-bold text-4xl leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-emerald-200 mt-1">{label}</div>
    </div>
  );
}

function LeaderboardCard({ data, config }) {
  const Icon = config.icon;
  const palettes = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    blue: "text-blue-700 bg-blue-50 border-blue-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    slate: "text-slate-700 bg-slate-50 border-slate-200",
    purple: "text-purple-700 bg-purple-50 border-purple-200",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid={`leaderboard-${config.key}`}>
      <div className="flex items-center gap-2 mb-4">
        <span className={`p-2 rounded-md border ${palettes[config.color]}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Classement</div>
          <div className="font-barlow font-bold text-base tracking-tight">{config.label}</div>
        </div>
      </div>
      {data.length === 0 ? (
        <div className="text-xs text-slate-400 py-6 text-center">Aucune donnée</div>
      ) : (
        <ul className="space-y-2">
          {data.map((p, i) => (
            <li key={p.player_id} data-testid={`${config.key}-${i + 1}`}
              className="flex items-center gap-3 py-1.5">
              <div className={`font-barlow font-bold text-lg w-7 text-center shrink-0 ${
                i === 0 ? "text-amber-500" : i === 1 ? "text-slate-400" : i === 2 ? "text-amber-700" : "text-slate-300"
              }`}>
                {i + 1}
              </div>
              <div className="h-7 w-7 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center overflow-hidden shrink-0">
                {p.photo_url ? (
                  <img src={toAuthUrl(p.photo_url)} alt="" className="h-full w-full object-cover" />
                ) : <User className="h-3.5 w-3.5 text-emerald-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{p.player_name}</div>
                <div className="text-[10px] text-slate-500">
                  #{p.jersey_number ?? "—"} • {p.category}
                </div>
              </div>
              <div className="text-right">
                <div className="font-barlow font-bold text-lg text-slate-900 leading-none">{p[config.stat]}</div>
                <div className="text-[9px] uppercase tracking-wider text-slate-400">{config.unit}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
